import JSZip from 'jszip';

export const BASE_URL = process.env.REACT_APP_BASE_URL || '/futurizer';
export const API_VERSION = process.env.REACT_APP_API_VERSION || 'v1';

const TOKEN_KEY = 'futurizer_token';

function parseJwtPayload(token) {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

function getToken() {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function authHeaders(extra = {}) {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

async function handleResponse(res) {
  if (!res.ok) {
    let msg = `Request failed (${res.status})`;
    try { const j = await res.json(); msg = j?.message ?? j?.title ?? msg; } catch {}
    throw new Error(msg);
  }
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

// ── 401 interceptor ─────────────────────────────────────────────────────────

let _pendingRefresh = null;
let _onSessionExpired = null;

export function setSessionExpiredHandler(fn) {
  _onSessionExpired = fn;
}

const REFRESH_TIMEOUT_MS = 10000;

async function doRefresh() {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);
  let res;
  try {
    res = await fetch(`${BASE_URL}/${API_VERSION}/Authentication/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: authHeaders(),
      signal: controller.signal,
    });
  } catch (err) {
    throw err.name === 'AbortError' ? new Error('Refresh timed out') : err;
  } finally {
    clearTimeout(timeoutId);
  }
  if (!res.ok) throw new Error('Refresh failed');
  const { token, userId } = await res.json();
  const payload = parseJwtPayload(token);
  const WCF_NAME = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name';
  const user = {
    id:    userId ?? payload?.Id ?? null,
    name:  payload?.[WCF_NAME] ?? '',
    email: payload?.email ?? '',
  };
  sessionStorage.setItem(TOKEN_KEY, token);
  _onSessionExpired?.({ type: 'TOKEN_REFRESHED', token, user });
  return token;
}

// Exported so RTK Query's custom baseQuery (src/store/futurizerApi.js) can reuse
// the exact same request semantics — including the silent refresh-on-401 retry —
// instead of duplicating this logic.
export async function fetchWithAuth(url, init = {}) {
  const res = await fetch(url, init);
  if (res.status !== 401) return res;
  // Never retry auth endpoints — avoids infinite refresh loop
  if (url.includes('/Authentication/')) return res;

  if (!_pendingRefresh) {
    _pendingRefresh = doRefresh()
      .catch(err => {
        _onSessionExpired?.({ type: 'SESSION_EXPIRED' });
        throw err;
      })
      .finally(() => { _pendingRefresh = null; });
  }

  try {
    await _pendingRefresh;
  } catch {
    throw new Error('Session expired. Please log in again.');
  }

  return fetch(url, {
    ...init,
    headers: { ...init.headers, Authorization: `Bearer ${getToken()}` },
  });
}

// ── Dummy data (no API endpoint available) ──────────────────────────────────

let DUMMY_USERS = [
  { id: 1, name: 'Admin User',  username: 'admin', email: 'admin@admin.com',   enabled: true, role: 'admin' },
  { id: 2, name: 'Jane Smith',  username: 'jane',  email: 'jane@example.com',  enabled: true, role: 'user'  },
  { id: 3, name: 'Marco Rossi', username: 'marco', email: 'marco@example.com', enabled: true, role: 'user'  },
  { id: 4, name: 'Normal User', username: 'user',  email: 'user@example.com',  enabled: true, role: 'user'  },
];

const SOURCE_TREE = {
  name: 'Source', type: 'folder',
  children: [
    {
      name: 'components', type: 'folder',
      children: [
        { name: 'Button.jsx',   type: 'file' },
        { name: 'DataGrid.jsx', type: 'file' },
        { name: 'Header.jsx',   type: 'file' },
        { name: 'Modal.jsx',    type: 'file' },
        { name: 'Sidebar.jsx',  type: 'file' },
      ],
    },
    {
      name: 'config', type: 'folder',
      children: [
        { name: 'app.config.json',       type: 'file' },
        { name: 'env.development.json',  type: 'file' },
        { name: 'routes.json',           type: 'file' },
      ],
    },
    {
      name: 'services', type: 'folder',
      children: [
        { name: 'api.js',          type: 'file' },
        { name: 'auth.service.js', type: 'file' },
        { name: 'http.js',         type: 'file' },
      ],
    },
    {
      name: 'ws_objects', type: 'folder',
      children: [
        { name: 'config.json', type: 'file' },
        { name: 'schema.xml',  type: 'file' },
        {
          name: 'templates', type: 'folder',
          children: [
            { name: 'base.html',   type: 'file' },
            { name: 'email.html',  type: 'file' },
            { name: 'report.html', type: 'file' },
          ],
        },
        {
          name: 'workflows', type: 'folder',
          children: [
            { name: 'approval.xml',     type: 'file' },
            { name: 'notification.xml', type: 'file' },
          ],
        },
      ],
    },
    { name: 'index.js',      type: 'file' },
    { name: 'package.json',  type: 'file' },
    { name: 'README.md',     type: 'file' },
  ],
};

// ── Auth ────────────────────────────────────────────────────────────────────

export async function apiLogin(identifier, password) {
  const trimmed = identifier.trim();

  const res = await fetch(`${BASE_URL}/${API_VERSION}/Authentication/usercredentials`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userName: trimmed, passWord: password }),
  });

  if (res.status === 404) throw new Error('Invalid credentials.');
  if (res.status === 403) throw new Error('Your account is not approved. Contact your administrator.');
  if (!res.ok) throw new Error('Login failed. Please try again.');

  const { token, userId } = await res.json();
  const payload = parseJwtPayload(token);
  const WCF_NAME = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name';
  const user = {
    id:    userId ?? payload?.Id ?? null,
    name:  payload?.[WCF_NAME] ?? identifier,
    email: payload?.email ?? (identifier.includes('@') ? identifier : ''),
  };
  return { token, user };
}

export async function apiLogout(token) {
  await fetch(`${BASE_URL}/${API_VERSION}/Authentication/logout`, {
    method: 'POST',
    credentials: 'include',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
}

export async function apiRefreshToken() {
  const res = await fetch(`${BASE_URL}/${API_VERSION}/Authentication/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Token refresh failed.');
  const { token, userId } = await res.json();
  const payload = parseJwtPayload(token);
  const WCF_NAME = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name';
  const user = {
    id:    userId ?? payload?.Id ?? null,
    name:  payload?.[WCF_NAME] ?? '',
    email: payload?.email ?? '',
  };
  sessionStorage.setItem(TOKEN_KEY, token);
  return { token, user };
}

// ── Projects ────────────────────────────────────────────────────────────────

async function fetchRawProjects() {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Project`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to fetch projects.');
  return res.json();
}

async function fetchRawCustomers() {
  try {
    const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Customer`, { headers: authHeaders() });
    return res.ok ? res.json() : [];
  } catch {
    return [];
  }
}

function mapApiProject(p, customerMap = {}) {
  return {
    id:             p.projectId,
    name:           p.projectName,
    customerId:     p.fkCustomerId,
    customer:       customerMap[p.fkCustomerId] ?? `Customer #${p.fkCustomerId}`,
    description:    p.description ?? '',
    status:         p.isActive ? 'active' : 'inactive',
    environment:    'Development',
    startDate:      p.startDate ?? '',
    createdAt:      p.createdat ?? '',
    lastActivity:   p.updatedat ?? p.createdat ?? '',
    updatedAt:      p.updatedat ?? null,
    standardsCount: 0,
    resourceCount:  p.resourceCount ?? 0,
    team:           [],
    projectCode:    p.projectCode,
  };
}

export async function apiGetAllProjects() {
  const [rawProjects, rawCustomers] = await Promise.all([fetchRawProjects(), fetchRawCustomers()]);
  const customerMap = Object.fromEntries(rawCustomers.map(c => [c.customerId, c.companyName ?? c.legalName]));
  return rawProjects.map(p => mapApiProject(p, customerMap));
}

export async function apiGetProjects(userId) {
  const [rawProjects, rawCustomers] = await Promise.all([fetchRawProjects(), fetchRawCustomers()]);
  const customerMap = Object.fromEntries(rawCustomers.map(c => [c.customerId, c.companyName ?? c.legalName]));

  let authorizedIds = null;
  try {
    const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/User/userproject/${userId}`, { headers: authHeaders() });
    if (res.ok) {
      const assignments = await res.json();
      authorizedIds = new Set(assignments.map(a => a.fkProjectId));
    }
  } catch {}

  const mapped = rawProjects.map(p => mapApiProject(p, customerMap));
  return authorizedIds && authorizedIds.size > 0
    ? mapped.filter(p => authorizedIds.has(p.id))
    : mapped;
}

// Maps a row from GET /User/userproject/{userId} directly to the project-card
// shape ProjectSelectPage expects. Unlike the /Project listing, this endpoint
// doesn't return a numeric fkCustomerId — only the customer's display name —
// so `customerId` stays null here; ProjectModal resolves it by name against
// the customer list when a project is opened for editing.
// Exported for src/store/futurizerApi.js's RTK Query endpoint to reuse.
export function mapUserProjectRow(a) {
  return {
    id:             a.fkProjectId,
    name:           a.projectName,
    customerId:     null,
    customer:       a.companyName ?? '',
    description:    '',
    status:         a.isActive ? 'active' : 'inactive',
    environment:    'Development',
    startDate:      a.startDate ?? '',
    createdAt:      a.createdat ?? '',
    lastActivity:   a.updatedat ?? a.createdat ?? '',
    updatedAt:      a.updatedat ?? null,
    standardsCount: a.activeStandardCount ?? 0,
    // processedCommandQueueCount is the field that actually carries a
    // meaningful number on this endpoint — resourceCount is always 0.
    resourceCount:  a.processedCommandQueueCount ?? a.resourceCount ?? 0,
    team:           [],
    projectCode:    a.projectCode,
  };
}

export async function apiCreateProject(fields) {
  const now = new Date().toISOString();
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Project`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      projectName:               fields.projectName,
      projectCode:               fields.projectCode               || null,
      fkCustomerId:              Number(fields.fkCustomerId)      || 0,
      description:               fields.description               || null,
      fkCodeListDomain:          Number(fields.fkCodeListDomain)  || null,
      fkCodeListSourcePlatform:  Number(fields.fkCodeListSourcePlatform) || null,
      fkCodeListPbVersion:       Number(fields.fkCodeListPbVersion)      || null,
      pblCount:                  fields.pblCount ? Number(fields.pblCount) : null,
      fkCodeListDesiredFrontend: Number(fields.fkCodeListDesiredFrontend) || null,
      frontendVersion:           fields.frontendVersion           || null,
      fkCodeListDesiredBackend:  Number(fields.fkCodeListDesiredBackend)  || null,
      backendVersion:            fields.backendVersion            || null,
      fkCodeListDesiredDatabase: Number(fields.fkCodeListDesiredDatabase) || null,
      targetRepoUrl:             fields.targetRepoUrl             || null,
      fkCodeListStatus:          Number(fields.fkCodeListStatus)  || null,
      fkCodeListPriority:        Number(fields.fkCodeListPriority)|| null,
      startDate:                 fields.startDate                 || null,
      targetEndDate:             fields.targetEndDate             || null,
      actualEndDate:             fields.actualEndDate             || null,
      projectAPIKey:             fields.projectAPIKey             || null,
      isActive:      1,
      createdat:     now,
      createdby:     0,
      updatedat:     now,
      updatedby:     0,
    }),
  });
  if (!res.ok) throw new Error('Failed to create project.');
  return res.json();
}

export async function apiUpdateProject(projectId, { name, customerId, description, startDate, userIds }) {
  const now = new Date().toISOString();
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Project/${projectId}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify({
      projectName:  name,
      fkCustomerId: Number(customerId) || 0,
      description:  description ?? '',
      startDate:    startDate || null,
      isActive:     1,
      updatedat:    now,
      updatedby:    0,
    }),
  });
  if (!res.ok) throw new Error('Failed to update project.');
  return handleResponse(res.clone ? res.clone() : res).catch(() => ({ projectId }));
}

export async function apiDeleteProject(projectId) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Project/${projectId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to delete project.');
}

// ── Customers ───────────────────────────────────────────────────────────────

export async function apiGetCustomers() {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Customer`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to fetch customers.');
  return res.json();
}

export async function apiGetCustomer(customerId) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Customer/${customerId}`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to fetch customer.');
  return res.json();
}

export async function apiCreateCustomer(data) {
  const now = new Date().toISOString();
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Customer`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ ...data, isActive: 1, createdat: now, createdby: 0, updatedat: now, updatedby: 0 }),
  });
  if (!res.ok) throw new Error('Failed to create customer.');
  return res.json();
}

export async function apiUpdateCustomer(customerId, data) {
  const now = new Date().toISOString();
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Customer/${customerId}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify({ ...data, updatedat: now, updatedby: 0 }),
  });
  if (!res.ok) throw new Error('Failed to update customer.');
  return handleResponse(res);
}

export async function apiDeleteCustomer(customerId) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Customer/${customerId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to delete customer.');
}

// ── CodeList ────────────────────────────────────────────────────────────────

export async function apiGetCodeList() {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/CodeList`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to fetch code list.');
  return res.json();
}

export async function apiGetCodeListByType(type) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/CodeList/type/${encodeURIComponent(type)}`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`Failed to fetch code list for type "${type}".`);
  return res.json();
}

export async function apiCreateCodeList(data) {
  const now = new Date().toISOString();
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/CodeList`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ ...data, isActive: 1, createdat: now, createdby: 0, updatedat: now, updatedby: 0 }),
  });
  if (!res.ok) throw new Error('Failed to create code list entry.');
  return res.json();
}

export async function apiUpdateCodeList(codeListId, data) {
  const now = new Date().toISOString();
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/CodeList/${codeListId}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify({ ...data, updatedat: now, updatedby: 0 }),
  });
  if (!res.ok) throw new Error('Failed to update code list entry.');
  return handleResponse(res);
}

export async function apiDeleteCodeList(codeListId) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/CodeList/${codeListId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to delete code list entry.');
}

// ── Resources ───────────────────────────────────────────────────────────────

export async function apiGetResources(projectId) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Resource/project/${projectId}`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to fetch resources.');
  return res.json();
}

export async function apiCreateResource(projectId, { resourceText, resourceNotes }, userId = 0) {
  let fkCodeListDownloadUpload = 100; // fallback
  try {
    const codes = await apiGetCodeListByType('DownloadUpload');
    const uploadEntry = codes.find(c => c.codeListSubType === 'Upload');
    if (uploadEntry) fkCodeListDownloadUpload = uploadEntry.codeListId;
  } catch {}

  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Resource`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ fkProjectId: projectId, fkCodeListDownloadUpload, resourceText, resourceNotes, createdBy: userId }),
  });
  if (!res.ok) throw new Error('Failed to create resource.');
  return res.json();
}

export async function apiUpdateResource(resourceId, { resourceText, resourceNotes, isActive = true }, userId = 0) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Resource/${resourceId}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify({ resourceText, resourceNotes, isActive, updatedBy: userId }),
  });
  if (!res.ok) throw new Error('Failed to update resource.');
  return handleResponse(res);
}

export async function apiDeleteResource(resourceId) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Resource/${resourceId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to delete resource.');
}

// ── Standards ────────────────────────────────────────────────────────────────

export async function apiGetStandards(projectId) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Project/${projectId}/standards`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to fetch standards.');
  return res.json();
}

export async function apiCreateStandard(projectId, { fkCodeListStandard, title, content }, userId = 0) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Project/standard`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      fkProjectId:        projectId,
      fkCodeListStandard: fkCodeListStandard ? Number(fkCodeListStandard) : null,
      title,
      content,
      createdBy:          userId,
    }),
  });
  if (!res.ok) throw new Error('Failed to create standard.');
  return res.json();
}

export async function apiUpdateStandard(projectStandardId, { fkCodeListStandard, title, content, isActive }, userId = 0) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Project/standard/${projectStandardId}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify({
      fkCodeListStandard: fkCodeListStandard ? Number(fkCodeListStandard) : null,
      title,
      content,
      isActive:           isActive ?? true,
      updatedBy:          userId,
    }),
  });
  if (!res.ok) throw new Error('Failed to update standard.');
  return handleResponse(res);
}

export async function apiDeleteStandard(projectStandardId) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Project/standard/${projectStandardId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to delete standard.');
}

// ── Users ───────────────────────────────────────────────────────────────────

export async function apiCreateUser({ name, username, email, password, enabled, projectIds }) {
  const now = new Date().toISOString();
  const [firstName, ...rest] = (name ?? '').split(' ');
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/User`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      userName:  username,
      email:     email,
      password:  password,
      firstName: firstName ?? '',
      lastName:  rest.join(' ') ?? '',
      isActive:  enabled ? 1 : 0,
      createdat: now,
      createdby: 0,
      updatedat: now,
      updatedby: 0,
    }),
  });
  if (!res.ok) throw new Error('Failed to create user.');
  const created = await res.json();
  for (const pid of (projectIds ?? [])) {
    await apiAssignUserProject(created.userId, pid).catch(() => {});
  }
  return { id: created.userId, name, username, email, enabled };
}

export async function apiUpdateUser(id, { name, username, email, enabled, projectIds }) {
  // No PUT /v1/User endpoint — update local dummy only
  const idx = DUMMY_USERS.findIndex(u => u.id === id);
  if (idx !== -1) {
    DUMMY_USERS[idx] = { ...DUMMY_USERS[idx], name, username, email, enabled };
  }
  return { id, name, username, email, enabled };
}

// PUT /User/{id}/change-password. Errors come back as a bare string (plain text,
// e.g. Old password is incorrect.), not a { message } object. On success the
// API deletes the refresh cookie, so the caller should sign the user out.
export async function apiChangePassword({ userId, currentPassword, newPassword, confirmPassword }) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/User/${userId}/change-password`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify({ oldPassword: currentPassword, newPassword, confirmPassword }),
  });
  if (!res.ok) {
    let msg = `Could not change password (${res.status}).`;
    const text = (await res.text().catch(() => '')).trim();
    if (text) {
      try {
        const body = JSON.parse(text);
        msg = typeof body === 'string' ? body : (body?.message ?? body?.title ?? msg);
      } catch {
        msg = text;
      }
    }
    throw new Error(msg);
  }
  return true;
}

export async function apiGetAllUsers() {
  // No GET /v1/User list endpoint — return dummy data
  return DUMMY_USERS.map(u => ({ ...u, projects: [] }));
}

export async function apiAssignUserProject(userId, projectId) {
  const now = new Date().toISOString();
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/User/userproject`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ fkUserId: userId, fkProjectId: projectId, isActive: 1, createdat: now, createdby: 0, updatedat: now, updatedby: 0 }),
  });
  if (!res.ok) throw new Error('Failed to assign user to project.');
  return res.json();
}

export async function apiRemoveUserProject(userProjectId) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/User/userproject/${userProjectId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to remove user-project assignment.');
}

export async function apiCreateUserSession(data) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/User/usersession`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to create user session.');
  return res.json();
}

// ── Commands (Core CRUD) ──────────────────────────────────────────────────────

export async function apiGetAllCommands() {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Command`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to fetch commands.');
  return res.json();
}

export async function apiGetCommand(commandId) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Command/${commandId}`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to fetch command.');
  return res.json();
}

export async function apiCreateCommand({ commandName, displayName, commandType, description, inputSchema, outputSchema, isActive = true, createdBy = 0 }) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Command`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ commandName, displayName, commandType, description, inputSchema, outputSchema, isActive, createdBy }),
  });
  if (!res.ok) throw new Error('Failed to create command.');
  return res.json();
}

export async function apiUpdateCommand(commandId, { commandName, displayName, commandType, description, inputSchema, outputSchema, isActive, updatedBy = 0 }) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Command/${commandId}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify({ commandName, displayName, commandType, description, inputSchema, outputSchema, isActive, updatedBy }),
  });
  if (!res.ok) throw new Error('Failed to update command.');
  return res.json();
}

export async function apiDeleteCommand(commandId) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Command/${commandId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to delete command.');
}

// ── Command Queue ─────────────────────────────────────────────────────────────

export async function apiGetAllCommandQueue() {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Command/queue`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to fetch command queue.');
  return res.json();
}

export async function apiGetCommandQueueEntry(commandQueueId) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Command/queue/${commandQueueId}`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to fetch queue entry.');
  return res.json();
}

export async function apiGetCommandQueueByCommand(commandId) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Command/${commandId}/queue`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to fetch queue for command.');
  return res.json();
}

export async function apiGetCommandQueue(projectId) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Command/queue/project/${projectId}`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to fetch command queue.');
  return res.json();
}

export async function apiAddToCommandQueue({ fkCommandId, fkProjectId, sourceFileInfo, userId }) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Command/queue`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      fkCommandId,
      fkProjectId,
      queueStatus:    'QUEUED',
      sourceFileInfo: sourceFileInfo || null,
      isActive:       true,
      createdBy:      userId ?? 0,
    }),
  });
  // handleResponse (not a hardcoded "Failed to add command to queue.") so a
  // real backend rejection reason — e.g. a payload/validation error more
  // likely with a large file selection — actually reaches the UI instead of
  // being discarded here.
  return handleResponse(res);
}

export async function apiUpdateCommandQueue(commandQueueId, { queueStatus, pickedAt, completedAt, processingNotes, sourceFileInfo, isActive, updatedBy = 0 }) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Command/queue/${commandQueueId}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify({ queueStatus, pickedAt, completedAt, processingNotes, sourceFileInfo, isActive, updatedBy }),
  });
  if (!res.ok) throw new Error('Failed to update queue entry.');
  return res.json();
}

export async function apiDeleteCommandQueue(commandQueueId) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Command/queue/${commandQueueId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to delete queue entry.');
}

export async function apiDownloadGeneratedCode(commandQueueId) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/ProcessCommandQueue/generatedCodeDownload/${commandQueueId}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to download generated files.');
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^;"]+)"?/i);
  const filename = match ? decodeURIComponent(match[1]) : `command-queue-${commandQueueId}.zip`;
  const blob = await res.blob();
  return { blob, filename };
}

// ── Project–Command Assignments ───────────────────────────────────────────────

export async function apiGetAllProjectCommandAssignments() {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Project/command`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to fetch project-command assignments.');
  return res.json();
}

export async function apiGetProjectCommands(projectId) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Project/${projectId}/commands`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to fetch project commands.');
  return res.json();
}

export async function apiAssignCommandToProject({ fkProjectId, fkCommandId, isActive = true, createdBy = 0 }) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Project/command`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ fkProjectId, fkCommandId, isActive, createdBy }),
  });
  if (!res.ok) throw new Error('Failed to assign command to project.');
  return res.json();
}

export async function apiUpdateProjectCommand(projectCommandId, { fkProjectId, fkCommandId, isActive, updatedBy = 0 }) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Project/command/${projectCommandId}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify({ fkProjectId, fkCommandId, isActive, updatedBy }),
  });
  if (!res.ok) throw new Error('Failed to update project-command assignment.');
  return res.json();
}

export async function apiDeleteProjectCommand(projectCommandId) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Project/command/${projectCommandId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to delete project-command assignment.');
}

// ── Prompts ───────────────────────────────────────────────────────────────────

export async function apiGetCommandPrompts(commandId) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Prompt/command/${commandId}`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to fetch prompts for command.');
  return res.json();
}

// ── Source ──────────────────────────────────────────────────────────────────

export async function apiGetSourceTree() {
  return SOURCE_TREE;
}

export async function apiGetProjectSourceTree(projectId) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/SourceCode/project/${projectId}/source-tree`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to fetch source tree.');
  return res.json();
}

export async function apiGetResource(resourceId) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/Resource/${resourceId}`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to fetch resource.');
  return res.json();
}

export async function apiUploadSource(projectId, zipFile) {
  const token = getToken();
  const formData = new FormData();
  formData.append('file', zipFile);
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/SourceCode/${projectId}/upload-source`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) throw new Error('Upload failed. Please try again.');
  return res.json();
}

// Deletes one or more files/folders from a project's uploaded source tree.
// `paths` are relative to the project's Source folder — the same relative
// paths tracked in selectedFiles and rendered by the source tree (e.g.
// "dw_priority_send_form.srd" or "some_folder/dw_priority_send_form.srd").
// Deleting a folder path removes everything under it. The backend reports a
// per-item outcome (a failure on one path — not found, escapes the Source
// folder, locked file, etc. — doesn't stop the others), which handleResponse
// hands back here for the caller to inspect if it needs to.
// Uploads one or more files into a specific folder of a project's source tree
// as a single ZIP (POST .../upload-zip). `uploads` is a list of { file, path } —
// `path` is the file's name, which may carry a sub-path (e.g. "common/n_util.sru"
// for a folder upload) that the backend recreates under the target folder. The
// files are zipped here before sending, except a lone .zip which is sent as-is
// (the backend extracts it). `folderPath` is relative to the project's Source
// folder, same form as the paths rendered by the source tree (e.g.
// "some_folder/sub_folder"); empty means the Source root. Existing files with
// the same name are overwritten. The backend reports a per-entry outcome —
// { targetPath, requestedCount, uploadedCount, failedCount, items: [{ fileName,
// relativePath, uploaded, overwritten, sizeBytes, errorMessage }] }, with
// fileName being the entry's path inside the ZIP — so a failure on one file
// doesn't stop the others; the caller inspects `items`.
export function isZipUpload(uploads) {
  return uploads.length === 1 && uploads[0].path.toLowerCase().endsWith('.zip');
}

export async function apiUploadFilesToFolder(projectId, folderPath, uploads) {
  let zipFile;
  if (isZipUpload(uploads)) {
    zipFile = uploads[0].file;
  } else {
    const zip = new JSZip();
    uploads.forEach(({ file, path }) => zip.file(path, file));
    const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
    zipFile = new File([blob], 'upload.zip', { type: 'application/zip' });
  }

  const token = getToken();
  const formData = new FormData();
  formData.append('folderPath', folderPath ?? '');
  formData.append('file', zipFile, zipFile.name);
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/SourceCode/${projectId}/upload-zip`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) {
    // Error bodies here are a plain message string rather than JSON.
    let msg = '';
    try { msg = (await res.text()).trim().replace(/^"|"$/g, ''); } catch {}
    try { const j = JSON.parse(msg); msg = j?.message ?? j?.title ?? msg; } catch {}
    throw new Error(msg || `Upload failed (${res.status}). Please try again.`);
  }
  return res.json();
}

export async function apiDeleteSourceFiles(projectId, paths) {
  const res = await fetchWithAuth(`${BASE_URL}/${API_VERSION}/SourceCode/project/${projectId}/source-items`, {
    method: 'DELETE',
    headers: authHeaders(),
    body: JSON.stringify({ paths }),
  });
  return handleResponse(res);
}
